import type { ReactNode } from "react";

/* Inline 24x24 stroke icons - keeps the row actions legible without emoji. */

interface StrokeProps {
  children: ReactNode;
  width?: number;
  join?: boolean;
}

function Stroke({ children, width = 1.9, join = true }: StrokeProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin={join ? "round" : undefined}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export const Icons = {
  listen: (
    <Stroke>
      <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
      <rect x="2" y="13.5" width="5" height="7.5" rx="2" />
      <rect x="17" y="13.5" width="5" height="7.5" rx="2" />
    </Stroke>
  ),
  replay: (
    <Stroke>
      <path d="M3 12a9 9 0 1 0 2.6-6.4" />
      <path d="M3 3v6h6" />
    </Stroke>
  ),
  reuse: (
    <Stroke>
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h11a5 5 0 0 1 0 10h-5" />
    </Stroke>
  ),
  remove: (
    <Stroke>
      <path d="M6 6l12 12M18 6 6 18" />
    </Stroke>
  ),
  skip: (
    <Stroke>
      <path d="M5 4l11 8-11 8z" />
      <path d="M19 5v14" />
    </Stroke>
  ),
  mute: (
    <Stroke>
      <path d="M11 5 6 9H2v6h4l5 4z" />
      <path d="m17 9 5 6M22 9l-5 6" />
    </Stroke>
  ),
  play: (
    <Stroke>
      <path d="M7 4.5 19 12 7 19.5z" />
    </Stroke>
  ),
  refresh: (
    <Stroke width={2}>
      <path d="M21 12a9 9 0 1 1-2.6-6.4" />
      <path d="M21 3v6h-6" />
    </Stroke>
  ),
  search: (
    <Stroke width={2} join={false}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.6-3.6" />
    </Stroke>
  ),
  clear: (
    <Stroke width={2} join={false}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Stroke>
  ),
  sun: (
    <Stroke width={2} join={false}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </Stroke>
  ),
  system: (
    <Stroke width={2}>
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </Stroke>
  ),
  moon: (
    <Stroke width={2}>
      <path d="M20 14.5A8.5 8.5 0 1 1 9.5 4a6.8 6.8 0 0 0 10.5 10.5z" />
    </Stroke>
  ),
  /* The overflow trigger on a feed row. On a touch screen the row's six
     actions become one of these plus a labelled sheet — an icon strip that
     wide is neither hittable nor guessable without a tooltip to hover. */
  more: (
    <Stroke width={2}>
      <circle cx="5" cy="12" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="19" cy="12" r="1.4" />
    </Stroke>
  ),
  star: (
    <Stroke>
      <path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />
    </Stroke>
  ),
  starred: (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth={1.9} strokeLinejoin="round" aria-hidden="true">
      <path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />
    </svg>
  ),
  send: (
    <Stroke>
      <path d="M4 12 20 4l-4 16-4-7z" />
      <path d="m12 13 8-9" />
    </Stroke>
  ),
  density: (
    <Stroke>
      <path d="M4 6h16M4 12h16M4 18h16" />
    </Stroke>
  ),
  popout: (
    <Stroke>
      <path d="M14 4h6v6" />
      <path d="M20 4 11 13" />
      <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
    </Stroke>
  ),
  settings: (
    <Stroke width={2}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </Stroke>
  ),
} as const;

export type IconName = keyof typeof Icons;

/* Sized by CSS (.voice-picker .search-icon), so it needs the class on the svg
   itself rather than on a wrapper. */
export function SearchIcon() {
  return (
    <svg
      className="search-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.6-3.6" />
    </svg>
  );
}

export function Brand({ name }: { name: string }) {
  return (
    <span className="brand">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <path d="M4 10v4M9 6v12M14 3v18M19 9v6" />
      </svg>
      <span className="brand-name">{name}</span>
    </span>
  );
}

interface IconButtonProps {
  icon: IconName;
  title: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
}

export function IconButton({ icon, title, onClick, className = "" }: IconButtonProps) {
  return (
    <button
      type="button"
      className={`icon-btn ${className}`.trim()}
      title={title}
      aria-label={title}
      onClick={onClick}
    >
      {Icons[icon]}
    </button>
  );
}
